import type { AnchorHTMLAttributes, FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useScrollToHash } from '@/components/generals/smooth-scroll'

interface AnchorLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    hash: string
    immediate?: boolean
    onNavigate?: () => void
}

/**
 * Link to a section of the home page. On the home page it glides there with the
 * smooth scroller, from any other page it navigates to /#section.
 */
export const AnchorLink: FC<AnchorLinkProps> = ({
    hash,
    immediate = false,
    onNavigate,
    onClick,
    children,
    ...rest
}): ReactNode => {
    const { pathname } = useRouter()
    const scrollToHash = useScrollToHash()

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)
        onNavigate?.()

        if (pathname !== '/' || event.metaKey || event.ctrlKey || event.shiftKey) return

        event.preventDefault()
        scrollToHash(hash, { immediate })
    }

    return (
        <Link href={`/${hash}`} onClick={handleClick} {...rest}>
            {children}
        </Link>
    )
}
